import type { ReactNode } from 'react';
import { useAuth, type AuthState } from '@/lib/auth/useAuth';
import { RequireAuth } from '@/lib/auth/RequireAuth';
import { AccessDenied } from '@/lib/auth/AccessDenied';

/** Boolean flags on the auth state that can gate a route. */
type AuthFlag = {
  [K in keyof AuthState]: AuthState[K] extends boolean ? K : never;
}[keyof AuthState];

interface RequireFlagProps {
  /** Name of the boolean flag on the auth state that grants access. */
  flag: AuthFlag;
  /** Title shown by {@link AccessDenied} when the flag is not set. */
  deniedTitle: string;
  /** Message shown by {@link AccessDenied} when the flag is not set. */
  deniedMessage: string;
  children: ReactNode;
}

/**
 * Gate a subtree behind a boolean auth flag.
 *
 * Always wraps {@link RequireAuth} so unauthenticated users go through the
 * normal login redirect before we ever evaluate the flag.
 */
export function RequireFlag({
  flag,
  deniedTitle,
  deniedMessage,
  children,
}: RequireFlagProps): JSX.Element {
  return (
    <RequireAuth>
      <FlagGate flag={flag} deniedTitle={deniedTitle} deniedMessage={deniedMessage}>
        {children}
      </FlagGate>
    </RequireAuth>
  );
}

function FlagGate({ flag, deniedTitle, deniedMessage, children }: RequireFlagProps): JSX.Element {
  const auth = useAuth();

  if (!auth[flag]) {
    return <AccessDenied title={deniedTitle} message={deniedMessage} />;
  }
  return <>{children}</>;
}
